import React, { useState, useEffect, useContext } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Alert, AlertDescription } from '../../ui/alert';
import { useToast } from '../../ui/use-toast';
import { 
  Users, 
  UserPlus,
  UserCheck,
  Crown,
  Clock,
  Activity,
  Mail,
  Phone,
  Calendar,
  TrendingUp,
  Edit,
  Trash2,
  Save,
  X,
  AlertTriangle,
  CheckCircle
} from 'lucide-react';
import { apiService, ApiUser, ApiTeam } from '../../../services/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { RealTimeContext } from '../../../contexts/RealTimeContext';

interface SupervisorStats {
  supervisor: ApiUser;
  team?: ApiTeam;
  teamMembersCount: number;
  activeWorkers: number;
  totalHoursToday: number;
  totalHoursWeek: number;
  performance: 'excellent' | 'good' | 'needs-improvement';
}

interface SupervisorsTabProps {
  monitorId: number;
}

export const SupervisorsTab: React.FC<SupervisorsTabProps> = ({
  monitorId
}) => {
  const [supervisors, setSupervisors] = useState<SupervisorStats[]>([]);
  const [availableSupervisors, setAvailableSupervisors] = useState<ApiUser[]>([]);
  const [teams, setTeams] = useState<ApiTeam[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [selectedSupervisor, setSelectedSupervisor] = useState<SupervisorStats | null>(null);
  const [newSupervisorName, setNewSupervisorName] = useState('');
  const [newSupervisorEmail, setNewSupervisorEmail] = useState('');
  const [selectedTeamForAssign, setSelectedTeamForAssign] = useState('');
  const [selectedSupervisorForAssign, setSelectedSupervisorForAssign] = useState('');
  const { toast } = useToast();

  useEffect(() => {
    loadSupervisorsData();

    // Listen for refresh events
    const handleRefresh = () => loadSupervisorsData();
    window.addEventListener('monitor-refresh', handleRefresh);
    
    return () => {
      window.removeEventListener('monitor-refresh', handleRefresh);
    };
  }, [monitorId]);

  const loadSupervisorsData = async () => {
    try {
      setIsLoading(true);
      
      // Get supervisors managed by this monitor
      const supervisorsList = await apiService.getSupervisorsByMonitor(monitorId);
      
      // Get all teams and users
      const [allTeams, allUsers, workEntriesResponse] = await Promise.all([
        apiService.getTeams(),
        apiService.getAllUsers(),
        apiService.getWorkEntries(new Date().toISOString().split('T')[0])
      ]);

      const workEntries = Array.isArray(workEntriesResponse) 
        ? workEntriesResponse 
        : (workEntriesResponse.data || []);

      // Get teams monitored by this monitor
      const monitoredTeams = allTeams.filter(t => t.monitorId === monitorId);
      setTeams(monitoredTeams);

      // Process supervisor stats
      const supervisorStats: SupervisorStats[] = await Promise.all(
        supervisorsList.map(async (supervisor) => {
          const team = monitoredTeams.find(t => t.supervisorId === supervisor.id);
          const teamMembers = team ? allUsers.filter(u => u.teamId === team.id) : [];
          
          // Get work entries for team members
          const teamMemberIds = teamMembers.map(m => m.id);
          const teamWorkEntries = workEntries.filter(entry => 
            teamMemberIds.includes(entry.userId)
          );

          // Calculate active workers using new status fields from User model
          const activeWorkers = teamMembers.filter(member => member.isWorking).length;

          // Calculate hours
          const totalHoursToday = teamWorkEntries.reduce((total, entry) => {
            if (entry.startTime) {
              const start = new Date(entry.startTime);
              const end = entry.endTime ? new Date(entry.endTime) : new Date();
              const hours = (end.getTime() - start.getTime()) / (1000 * 60 * 60);
              return total + hours - (entry.totalBreakMinutes || 0) / 60;
            }
            return total;
          }, 0);

          // Calculate week hours (simplified - in real app would query last 7 days)
          const totalHoursWeek = totalHoursToday * 5; // Placeholder

          // Determine performance
          let performance: 'excellent' | 'good' | 'needs-improvement' = 'good';
          if (teamMembers.length > 0) {
            const productivity = (activeWorkers / teamMembers.length) * 100;
            if (productivity >= 80) performance = 'excellent';
            else if (productivity < 50) performance = 'needs-improvement';
          }

          return {
            supervisor,
            team,
            teamMembersCount: teamMembers.length,
            activeWorkers,
            totalHoursToday,
            totalHoursWeek,
            performance
          };
        })
      );

      setSupervisors(supervisorStats);

      // Get available supervisors (not assigned to any team)
      const availableSupers = allUsers.filter(u => 
        u.role === 'supervisor' && 
        !monitoredTeams.some(t => t.supervisorId === u.id)
      );
      setAvailableSupervisors(availableSupers);

    } catch (error) {
      console.error('Error loading supervisors data:', error);
      toast({
        title: 'Eroare',
        description: 'Nu s-au putut încărca datele supervisorilor',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateSupervisor = async () => {
    if (!newSupervisorName.trim()) {
      toast({
        title: 'Atenție',
        description: 'Introduceți numele supervisorului',
        variant: 'destructive'
      });
      return;
    }

    try {
      setIsLoading(true);
      
      // Generate a simple password
      const password = '1234'; // In production, generate secure password
      
      await apiService.createUser({
        name: newSupervisorName,
        role: 'supervisor',
        email: newSupervisorEmail || undefined,
        monitorId: monitorId
      });

      toast({
        title: 'Succes',
        description: `Supervisor creat cu parola: ${password}`,
      });

      setShowCreateDialog(false);
      setNewSupervisorName('');
      setNewSupervisorEmail('');
      await loadSupervisorsData();
    } catch (error) {
      console.error('Error creating supervisor:', error);
      toast({
        title: 'Eroare',
        description: 'Nu s-a putut crea supervisorul',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleAssignSupervisor = async () => {
    if (!selectedTeamForAssign || !selectedSupervisorForAssign) {
      toast({
        title: 'Atenție',
        description: 'Selectați echipa și supervisorul',
        variant: 'destructive'
      });
      return;
    }

    try {
      setIsLoading(true);
      
      await apiService.updateTeam(parseInt(selectedTeamForAssign), {
        supervisorId: parseInt(selectedSupervisorForAssign)
      });

      toast({
        title: 'Succes',
        description: 'Supervisor asignat cu succes',
      });

      setShowAssignDialog(false);
      setSelectedTeamForAssign('');
      setSelectedSupervisorForAssign('');
      await loadSupervisorsData();
    } catch (error) {
      console.error('Error assigning supervisor:', error);
      toast({
        title: 'Eroare',
        description: 'Nu s-a putut asigna supervisorul',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getPerformanceBadge = (performance: SupervisorStats['performance']) => {
    switch (performance) {
      case 'excellent':
        return <Badge variant="success">Excelent</Badge>;
      case 'good':
        return <Badge variant="default">Bun</Badge>;
      case 'needs-improvement':
        return <Badge variant="warning">Necesită îmbunătățire</Badge>;
    }
  };

  const getPerformanceColor = (performance: SupervisorStats['performance']) => {
    switch (performance) {
      case 'excellent':
        return 'text-green-600';
      case 'good':
        return 'text-blue-600';
      case 'needs-improvement':
        return 'text-orange-600';
    }
  };

  // Summary stats
  const totalSupervisors = supervisors.length;
  const excellentPerformers = supervisors.filter(s => s.performance === 'excellent').length;
  const totalTeamsManaged = supervisors.filter(s => s.team).length;
  const totalWorkersManaged = supervisors.reduce((sum, s) => sum + s.teamMembersCount, 0);

  return (
    <div className="space-y-6">
      {/* Actions Bar */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Gestionare Supervisori</h2>
        <div className="flex items-center gap-2">
          <Button
            onClick={() => setShowCreateDialog(true)}
            disabled={isLoading}
            className="flex items-center gap-2"
          >
            <UserPlus className="h-4 w-4" />
            Supervisor Nou
          </Button>
          
          <Button
            onClick={() => setShowAssignDialog(true)}
            disabled={isLoading || availableSupervisors.length === 0}
            variant="outline"
            className="flex items-center gap-2"
          >
            <UserCheck className="h-4 w-4" />
            Asignează Supervisor
          </Button>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-col items-center text-center">
              <Crown className="h-8 w-8 text-purple-500 mb-2" />
              <p className="text-sm text-gray-600">Total Supervisori</p>
              <p className="text-2xl font-bold">{totalSupervisors}</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-col items-center text-center">
              <TrendingUp className="h-8 w-8 text-green-500 mb-2" />
              <p className="text-sm text-gray-600">Performanță Excelentă</p>
              <p className="text-2xl font-bold">{excellentPerformers}</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-col items-center text-center">
              <Users className="h-8 w-8 text-blue-500 mb-2" />
              <p className="text-sm text-gray-600">Echipe Gestionate</p>
              <p className="text-2xl font-bold">{totalTeamsManaged}</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-col items-center text-center">
              <Activity className="h-8 w-8 text-orange-500 mb-2" />
              <p className="text-sm text-gray-600">Muncitori Total</p>
              <p className="text-2xl font-bold">{totalWorkersManaged}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Supervisors Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {supervisors.map((supervisor) => (
          <Card 
            key={supervisor.supervisor.id} 
            className="hover:shadow-lg transition-shadow"
          >
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span className="text-lg">{supervisor.supervisor.name}</span>
                {getPerformanceBadge(supervisor.performance)}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Team Info */}
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Echipă:</span>
                  <span className="font-medium">{supervisor.team?.name || 'Neasignat'}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Membri echipă:</span>
                  <span className="font-medium">{supervisor.teamMembersCount}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Activi acum:</span>
                  <span className={`font-medium ${getPerformanceColor(supervisor.performance)}`}>
                    {supervisor.activeWorkers}/{supervisor.teamMembersCount}
                  </span>
                </div>
              </div>

              {/* Hours Stats */}
              <div className="grid grid-cols-2 gap-2">
                <Card className="bg-gray-50">
                  <CardContent className="pt-3 pb-3">
                    <div className="text-center">
                      <Clock className="h-4 w-4 text-gray-600 mx-auto mb-1" />
                      <p className="text-xs text-gray-600">Ore azi</p>
                      <p className="font-bold">{supervisor.totalHoursToday.toFixed(1)}h</p>
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-gray-50">
                  <CardContent className="pt-3 pb-3">
                    <div className="text-center">
                      <Calendar className="h-4 w-4 text-gray-600 mx-auto mb-1" />
                      <p className="text-xs text-gray-600">Ore săptămână</p>
                      <p className="font-bold">{supervisor.totalHoursWeek.toFixed(0)}h</p>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Contact Info */}
              {supervisor.supervisor.email && (
                <div className="pt-2 border-t">
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Mail className="h-3 w-3" />
                    <span className="truncate">{supervisor.supervisor.email}</span>
                  </div>
                </div>
              )}

              {/* Actions */}
              <Button
                variant="outline"
                size="sm"
                className="w-full"
                onClick={() => setSelectedSupervisor(supervisor)}
              >
                Vezi Detalii
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>

      {supervisors.length === 0 && !isLoading && (
        <Card>
          <CardContent className="py-12 text-center">
            <Crown className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">Nu există supervisori de monitorizat</p>
            <Button
              onClick={() => setShowCreateDialog(true)}
              className="mt-4"
            >
              <UserPlus className="h-4 w-4 mr-2" />
              Creează Primul Supervisor
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Available Supervisors Alert */}
      {availableSupervisors.length > 0 && (
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            Există {availableSupervisors.length} supervisori disponibili care nu sunt asignați unei echipe.
            <Button
              size="sm"
              variant="link"
              className="ml-2"
              onClick={() => setShowAssignDialog(true)}
            >
              Asignează acum
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Create Supervisor Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Creare Supervisor Nou</DialogTitle>
            <DialogDescription>
              Creați un nou cont de supervisor pentru monitorizare
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Nume complet</Label>
              <Input
                value={newSupervisorName}
                onChange={(e) => setNewSupervisorName(e.target.value)}
                placeholder="Ex: Ion Popescu"
              />
            </div>
            
            <div>
              <Label>Email (opțional)</Label>
              <Input
                type="email"
                value={newSupervisorEmail}
                onChange={(e) => setNewSupervisorEmail(e.target.value)}
                placeholder="supervisor@example.com"
              />
            </div>

            <Alert>
              <CheckCircle className="h-4 w-4" />
              <AlertDescription>
                Parola implicită va fi "1234". Supervisorul o poate schimba la prima autentificare.
              </AlertDescription>
            </Alert>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowCreateDialog(false);
                setNewSupervisorName('');
                setNewSupervisorEmail('');
              }}
            >
              Anulează
            </Button>
            <Button
              onClick={handleCreateSupervisor}
              disabled={isLoading || !newSupervisorName.trim()}
            >
              Creează Supervisor
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Supervisor Dialog */}
      <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Asignare Supervisor la Echipă</DialogTitle>
            <DialogDescription>
              Selectați un supervisor disponibil și echipa la care doriți să îl asignați
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Supervisor</Label>
              <Select value={selectedSupervisorForAssign} onValueChange={setSelectedSupervisorForAssign}>
                <SelectTrigger>
                  <SelectValue placeholder="Selectează supervisor" />
                </SelectTrigger>
                <SelectContent>
                  {availableSupervisors.map(supervisor => (
                    <SelectItem key={supervisor.id} value={supervisor.id.toString()}>
                      {supervisor.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Echipă</Label>
              <Select value={selectedTeamForAssign} onValueChange={setSelectedTeamForAssign}>
                <SelectTrigger>
                  <SelectValue placeholder="Selectează echipa" />
                </SelectTrigger>
                <SelectContent>
                  {teams.filter(t => !t.supervisorId).map(team => (
                    <SelectItem key={team.id} value={team.id.toString()}>
                      {team.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {teams.filter(t => !t.supervisorId).length === 0 && (
              <Alert>
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  Toate echipele au deja supervisori asignați.
                </AlertDescription>
              </Alert>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowAssignDialog(false);
                setSelectedTeamForAssign('');
                setSelectedSupervisorForAssign('');
              }}
            >
              Anulează
            </Button>
            <Button
              onClick={handleAssignSupervisor}
              disabled={isLoading || !selectedTeamForAssign || !selectedSupervisorForAssign}
            >
              Asignează
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Supervisor Details Dialog */}
      {selectedSupervisor && (
        <Dialog open={!!selectedSupervisor} onOpenChange={() => setSelectedSupervisor(null)}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center justify-between">
                <span>{selectedSupervisor.supervisor.name}</span>
                {getPerformanceBadge(selectedSupervisor.performance)}
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4">
              {/* Performance Overview */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Performanță Generală</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="text-center">
                      <p className="text-sm text-gray-600">Productivitate</p>
                      <p className={`text-2xl font-bold ${getPerformanceColor(selectedSupervisor.performance)}`}>
                        {selectedSupervisor.teamMembersCount > 0 
                          ? Math.round((selectedSupervisor.activeWorkers / selectedSupervisor.teamMembersCount) * 100)
                          : 0}%
                      </p>
                    </div>
                    <div className="text-center">
                      <p className="text-sm text-gray-600">Ore azi</p>
                      <p className="text-2xl font-bold">{selectedSupervisor.totalHoursToday.toFixed(1)}h</p>
                    </div>
                    <div className="text-center">
                      <p className="text-sm text-gray-600">Media/membru</p>
                      <p className="text-2xl font-bold">
                        {selectedSupervisor.teamMembersCount > 0 
                          ? (selectedSupervisor.totalHoursToday / selectedSupervisor.teamMembersCount).toFixed(1)
                          : 0}h
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Team Details */}
              {selectedSupervisor.team && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Echipa Gestionată</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Nume echipă:</span>
                        <span className="font-medium">{selectedSupervisor.team.name}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Total membri:</span>
                        <span className="font-medium">{selectedSupervisor.teamMembersCount}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Membri activi:</span>
                        <span className="font-medium text-green-600">
                          {selectedSupervisor.activeWorkers}
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Contact Information */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Informații Contact</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {selectedSupervisor.supervisor.email && (
                    <div className="flex items-center gap-2">
                      <Mail className="h-4 w-4 text-gray-600" />
                      <span>{selectedSupervisor.supervisor.email}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <Crown className="h-4 w-4 text-gray-600" />
                    <span>Rol: Supervisor</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-gray-600" />
                    <span>ID: {selectedSupervisor.supervisor.id}</span>
                  </div>
                </CardContent>
              </Card>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};